import { useState } from 'react';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  date: string;
  category: string;
  image: string;
  author: string;
}

// ブログ記事データ（現在は空）
const blogPosts: BlogPost[] = [];

const categories = ["すべて", "製造ノウハウ", "ECサイト運営", "商品企画", "活用事例"];

export default function Blog() {
  const [selectedCategory, setSelectedCategory] = useState("すべて");
  const [searchTerm, setSearchTerm] = useState("");

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === "すべて" || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handlePostClick = (postId: number) => {
    window.REACT_APP_NAVIGATE(`/blog/${postId}`);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1>
                <img 
                  src="https://static.readdy.ai/image/4f6a1d7b5058e6da57f4192877d570f4/8397292e65571c64257096a9f31fb3e1.png" 
                  alt="合同会社サクラネクサスメディア" 
                  className="h-24"
                />
              </h1>
            </div>
            
            <nav className="hidden md:flex space-x-8 items-center">
              <a href="/" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer font-medium">ホーム</a>
              <a href="/#about" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer font-medium">会社概要</a>
              <a href="/#services" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer font-medium">サービス</a>
              <a href="/#company" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer font-medium">会社情報</a>
              <a href="/blog" className="text-blue-600 font-medium cursor-pointer">ブログ</a>
              <a href="/#contact" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer font-medium">お問い合わせ</a>
            </nav>

            <button className="md:hidden w-6 h-6 flex items-center justify-center cursor-pointer">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">ブログ</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            製造ノウハウやECサイト運営のコツなど、お役立ち情報を発信しています
          </p>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-6 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-96">
              <input
                type="text"
                placeholder="記事を検索..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus-border-transparent text-sm"
              />
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                <i className="ri-search-line text-gray-400"></i>
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    selectedCategory === category
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 記事がない場合の表示 */}
          <div className="text-center py-20">
            <i className="ri-article-line text-8xl text-gray-300 mb-6"></i>
            <h3 className="text-2xl font-semibold text-gray-600 mb-4">ブログ記事を準備中です</h3>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">
              現在、お客様に役立つ情報をお届けするためのブログ記事を準備しております。<br />
              近日中に公開予定ですので、今しばらくお待ちください。
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">お困りのことはございませんか？</h2>
          <p className="text-xl text-blue-100 mb-8">
            製造やECサイト運営に関するご相談は、お気軽にお問い合わせください
          </p>
          <a
            href="/#contact"
            className="inline-flex items-center bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors cursor-pointer"
          >
            <i className="ri-mail-line mr-2"></i>
            お問い合わせ
            <i className="ri-arrow-right-circle-line ml-2"></i>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h4 className="text-lg font-semibold mb-4">クイックリンク</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/" className="hover:text-white transition-colors cursor-pointer">ホーム</a></li>
                <li><a href="/#about" className="hover:text-white transition-colors cursor-pointer">会社概要</a></li>
                <li><a href="/#services" className="hover:text-white transition-colors cursor-pointer">サービス</a></li>
                <li><a href="/blog" className="hover:text-white transition-colors cursor-pointer">ブログ</a></li>
                <li><a href="/#contact" className="hover:text-white transition-colors cursor-pointer">お問い合わせ</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">会社情報</h4>
              <p className="text-gray-400 mb-4">
                合同会社サクラネクサスメディア
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">お問い合わせ</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-start">
                  <i className="ri-map-pin-line mr-2 mt-1"></i>
                  <span>
                    〒103-0027 東京都中央区日本橋1丁目12-4
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 合同会社サクラネクサスメディア. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
